// File: frontend/src/components/marketing/visuals/MarketingVoucherVisual.js
//
// Marketing recreation of the PesaFii voucher flow: available/redeemed/expired
// counts, a small batch of voucher cards with a status that transitions from
// Available → Redeemed once in view, and a redemption progress bar. Fictional
// codes/data; no API calls.

import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Stack, LinearProgress, useTheme, alpha } from "@mui/material";
import { motion } from "framer-motion";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import IosShareIcon from "@mui/icons-material/IosShare";
import PrintIcon from "@mui/icons-material/Print";
import { MarketingStatusBadge, MarketingMetricCard, SampleNote } from "./primitives";
import { useInViewOnce } from "../motion";
import { useReducedMotion } from "framer-motion";
import { DEMO_VOUCHERS, DEMO_VOUCHER_STATS, fmtUGX } from "../../../data/marketingDemoData";

const MoBox = motion(Box);

export default function MarketingVoucherVisual() {
  const theme = useTheme();
  const reduce = useReducedMotion();
  const [ref, inView] = useInViewOnce();
  const green = theme.palette.primary.main;

  // Once in view, flip the first voucher from Available -> Redeemed for the
  // "voucher redeemed" story (skipped under reduced motion).
  const [redeemedFirst, setRedeemedFirst] = useState(false);
  useEffect(() => {
    if (!inView || reduce) return;
    const t = setTimeout(() => setRedeemedFirst(true), 1400);
    return () => clearTimeout(t);
  }, [inView, reduce]);

  const total = DEMO_VOUCHER_STATS.available + DEMO_VOUCHER_STATS.redeemed;
  const redeemedPct = Math.round((DEMO_VOUCHER_STATS.redeemed / total) * 100);

  return (
    <Box ref={ref}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1, mb: 1.5 }}>
        <MarketingMetricCard label="Available" value={DEMO_VOUCHER_STATS.available} active={inView} color={green} />
        <MarketingMetricCard label="Redeemed" value={DEMO_VOUCHER_STATS.redeemed} active={inView} color={theme.palette.info.main} />
        <MarketingMetricCard label="Expired" value={DEMO_VOUCHER_STATS.expired} active={inView} color={theme.palette.error.main} />
      </Box>

      <Paper sx={{ p: 1.75, borderRadius: 2.5, border: "1px solid", borderColor: "divider", mb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary" }}>Redemption rate</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: green }}>{redeemedPct}%</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={inView ? redeemedPct : 0}
          sx={{ height: 6, borderRadius: 99, "& .MuiLinearProgress-bar": { bgcolor: green, borderRadius: 99, transition: "transform 1.1s cubic-bezier(0.22,1,0.36,1)" } }} />
      </Paper>

      <Stack spacing={1}>
        {DEMO_VOUCHERS.map((v, i) => {
          const status = i === 0 && redeemedFirst ? "redeemed" : v.status;
          return (
            <MoBox key={v.code}
              initial={reduce ? false : { opacity: 0, y: 14 }}
              animate={inView ? { opacity: 1, y: 0 } : (reduce ? {} : { opacity: 0, y: 14 })}
              transition={{ duration: 0.45, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              <Paper sx={{ p: 1.5, borderRadius: 2.5, border: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 34, height: 34, borderRadius: 1.5, flexShrink: 0, bgcolor: alpha(green, 0.14), color: green, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <ConfirmationNumberIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontSize: 13, fontWeight: 800, color: "text.primary", fontFamily: "monospace", letterSpacing: "0.02em" }}>{v.code}</Typography>
                  <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{v.plan} · {fmtUGX(v.price)}</Typography>
                </Box>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  <MarketingStatusBadge status={status} />
                  <IosShareIcon sx={{ fontSize: 15, color: "text.disabled", display: { xs: "none", sm: "block" } }} />
                  <PrintIcon sx={{ fontSize: 15, color: "text.disabled", display: { xs: "none", sm: "block" } }} />
                </Stack>
              </Paper>
            </MoBox>
          );
        })}
      </Stack>
      <SampleNote />
    </Box>
  );
}
